import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { baseQuery } from '../../../services/baseQuery';
import type {
  DataSourceLoadOptions,
  GridData,
  SaveEntityRequest,
  SaveEntityResponse,
  ColumnMetadata,
  Entity,
  LookupDataItem,
  UpdateBatchRequest,
  RelationCommandMetadata,
  SalaryCalculateMutationRequest,
  CreateTasksForTeamRequest,
  AutomaticWorkLogEmployeeOption,
  CreateTasksAndWorkLogsInput,
  CreateTasksAndWorkLogsResult,
  GenerateInvoicesRequest,
  TaskPriority,
  SortDescriptor,
  MissingEmployeeRatesRequest,
  MissingEmployeeRatesResult,
  CreateMissingEmployeeRateRequest,
  CreateMissingEmployeeRateResult,
  GenerateAutoInvoiceRequest,
  GenerateAutoInvoiceResult,
} from '../types';
import type { DetailMetadata, GetDetailMetadataParams } from '../../detail-form/types';
import type { ApiResponse } from '../../menu';
import type { PayrollDeliveryRequest, PayrollDeliveryResponse } from '../types/payroll';
import { flattenRawData, toCamelCase, normalizeBooleanish } from '../utils';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type {
  SpecField,
  SpecType,
  GetSpecificationResponse,
  SaveSpecificationRequest,
} from '../types/specificationTypes';

const GRID_API = '/umbraco/api/grid';
const TRACKER_API = '/umbraco/api/TrackerApi';

type LookupData = {
  [key: string]: string;
};

type LookupResponse = {
  map: LookupData;
  array: LookupDataItem[];
  allMap?: LookupData;
  allArray?: LookupDataItem[];
};

const getDefaultFilterValue = (filter: NonNullable<ColumnMetadata['defaultFilters']>[number]) => ({
  columnName: filter.ColumnName ?? filter.columnName,
  operator: filter.Operator ?? filter.operator,
  value: filter.Value ?? filter.value,
});

const buildFilterLookupMap = (
  lookupResponse: LookupResponse,
  defaultFilters: ColumnMetadata['defaultFilters']
): LookupData => {
  if (!defaultFilters?.length || !lookupResponse.array.length) return lookupResponse.map;

  const filteredIds = new Set(
    lookupResponse.array
      .filter((item) =>
        defaultFilters.every((filter) => {
          const { columnName, operator, value } = getDefaultFilterValue(filter);
          if (!columnName || (operator ?? '=') !== '=') return true;
          const field = item.Fields.find((candidate) => candidate.Name === columnName);
          const fieldValue =
            field?.StringValue ?? (item as unknown as Record<string, unknown>)[columnName];
          return (
            fieldValue !== undefined &&
            normalizeBooleanish(String(fieldValue ?? '')) ===
              normalizeBooleanish(String(value ?? ''))
          );
        })
      )
      .map((item) => String(item.Id))
  );

  return Object.fromEntries(
    Object.entries(lookupResponse.map).filter(([id]) => filteredIds.has(String(id)))
  );
};

type LookupParams = {
  nodeKey: string;
  fieldKey: string;
  displayFieldName?: string;
  defaultFilters?: ColumnMetadata['defaultFilters'];
  objectId?: string | number;
  parentId?: string | number;
  forDropdown?: boolean;
  /** Generic parent-id filter for dependent lookups. Maps to backend's `selectedEntity` query param. */
  selectedEntity?: string;
};

type CopyRowResponse = {
  Code: number;
  Persisted?: boolean;
  Data?: unknown;
  NewKeys?: string[];
  KeyField?: string;
};

type GridDataParams = {
  options: DataSourceLoadOptions;
  columnMetadata?: ColumnMetadata[];
};

type DropdownGridDataParams = {
  gridId: string;
  skip: number;
  take: number;
  filter?: unknown;
  sort?: SortDescriptor[];
};

const LOOKUP_EDITORS = new Set(['selectgrid', 'selectlookup']);

const isLookupColumn = (col: ColumnMetadata): boolean =>
  !!col.key &&
  !!col.dataField &&
  !!col.additionalData?.displayExpr &&
  LOOKUP_EDITORS.has(col.editor?.toLowerCase() ?? '');

type DeleteScreenshotResponse = {
  Success?: boolean;
  success?: boolean;
  Error?: { ErrorCode?: string; Message?: string };
};

type CurrencyRateRequest = {
  date: string;
  nameApi: string;
  fromCurrency: string;
  toCurrency: string;
};

type CurrencyRateResponse = {
  Code: number;
  data: number;
};

function buildGridDataCacheKey(endpointName: string, queryArgs: GridDataParams): string {
  const options = queryArgs.options;
  const relationFieldSignature = (queryArgs.columnMetadata ?? [])
    .filter(isLookupColumn)
    .map((col) => `${col.key}:${col.dataField}:${String(col.additionalData?.displayExpr ?? '')}`)
    .sort()
    .join('|');

  return `${endpointName}|${JSON.stringify({
    nodeKey: options.nodeKey,
    parentId: options.parentId,
    startDate: options.startDate,
    endDate: options.endDate,
    toShowDeleted: options.toShowDeleted,
    rowKeys: options.rowKeys,
    skip: options.skip,
    take: options.take,
    sort: options.sort,
    filter: options.filter,
    fieldKeys: options.fieldKeys,
    relationFieldSignature,
  })}`;
}

/**
 * Extract rawData, totalCount and summary from the backend's nested response structure.
 * Returns an error string when the response indicates failure.
 */
function extractGridResponse(response: Record<string, unknown>): {
  rawData: unknown[];
  totalCount: number;
  summary?: string[];
  error?: string;
} {
  if (response.code === 1 || response.Code === 1) {
    const innerData = (response.data || response.Data) as Record<string, unknown>;
    return {
      rawData: (innerData.data || innerData.Data || innerData) as unknown[],
      totalCount: (innerData.totalCount || innerData.TotalCount || 0) as number,
      summary: (innerData.summary || innerData.Summary) as string[] | undefined,
    };
  }
  if (response.data || response.Data) {
    return {
      rawData: (response.data || response.Data || []) as unknown[],
      totalCount: (response.totalCount || response.TotalCount || 0) as number,
      summary: (response.summary || response.Summary) as string[] | undefined,
    };
  }
  return {
    rawData: [],
    totalCount: 0,
    error: (response.Error ||
      response.error ||
      response.message ||
      'Failed to load grid data') as string,
  };
}

const parseSalaryResponse = (response: unknown): Record<string, unknown>[] => {
  const payload = response as Record<string, unknown>;
  const responseData = payload.data ?? payload.Data;
  const nestedData = responseData as Record<string, unknown> | undefined;
  const rows = Array.isArray(responseData)
    ? responseData
    : nestedData && (nestedData.data ?? nestedData.Data);

  if (nestedData && Number(nestedData.Code ?? nestedData.code) < 1) {
    throw new Error(extractErrorMessage(nestedData, 'Salary calculation failed'));
  }

  if (!Array.isArray(rows)) {
    throw new Error(extractErrorMessage(payload, 'Salary calculation failed'));
  }

  return flattenRawData(rows);
};

const parseGenerateInvoicesResponse = (response: unknown): string => {
  const payload = response as Record<string, unknown>;
  const responseData = payload.data ?? payload.Data;
  const nestedData = responseData as Record<string, unknown> | undefined;
  const code = Number(nestedData?.Code ?? nestedData?.code ?? payload.Code ?? payload.code);

  if (code !== 1) {
    throw new Error(extractErrorMessage(payload, 'Failed to generate invoices.'));
  }

  const message = nestedData?.data ?? nestedData?.Data ?? responseData;
  if (typeof message !== 'string') {
    throw new Error('Failed to generate invoices.');
  }

  return message;
};

/**
 * RTK Query API slice for grid data endpoints
 * Provides automatic caching for grid data and lookup values
 */
export const gridApi = createApi({
  reducerPath: 'gridApi',
  baseQuery,
  tagTypes: ['GridData', 'Lookup', 'DetailMetadata', 'Entity'],
  endpoints: (builder) => ({
    getRelationCommandMetadata: builder.query<RelationCommandMetadata, { commandKey: string }>({
      query: ({ commandKey }) => ({
        url: '/umbraco/api/RelationCommand/GetCommandMetadata',
        params: { commandKey },
      }),
      transformResponse: (response: ApiResponse<RelationCommandMetadata>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as RelationCommandMetadata;
        }
        throw new Error(response.message || 'Failed to load relation command configuration');
      },
    }),
    calculateSalary: builder.mutation<Record<string, unknown>[], SalaryCalculateMutationRequest>({
      query: ({ request, url }) => ({
        url,
        method: 'POST',
        body: request,
      }),
      transformResponse: parseSalaryResponse,
    }),
    generateInvoices: builder.mutation<string, GenerateInvoicesRequest>({
      query: (request) => ({
        url: '/umbraco/api/GenerateInvoices/GenerateInvoices',
        method: 'POST',
        body: request,
      }),
      transformResponse: parseGenerateInvoicesResponse,
    }),
    generateAutoInvoice: builder.mutation<GenerateAutoInvoiceResult, GenerateAutoInvoiceRequest>({
      query: (request) => ({
        url: '/umbraco/api/GenerateAutoInvoice/Generate',
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: unknown) => {
        const payload = response as Record<string, unknown>;
        const code = Number(payload.Code ?? payload.code ?? 0);
        if (code !== 1) {
          throw new Error(extractErrorMessage(payload, 'Failed to generate auto invoice.'));
        }
        // Backend returns InvoicesCreated, InvoiceId, FailedEmployees at top level
        // alongside "data" string message and "Code"
        return {
          message: String(payload.data ?? payload.Data ?? 'Done.'),
          invoicesCreated: Number(payload.InvoicesCreated ?? payload.invoicesCreated ?? 0),
          invoiceId:
            payload.InvoiceId == null && payload.invoiceId == null
              ? null
              : Number(payload.InvoiceId ?? payload.invoiceId),
          failedEmployees: (
            (payload.FailedEmployees ?? payload.failedEmployees ?? []) as unknown[]
          ).map(String),
        };
      },
    }),
    getTaskPriorities: builder.query<TaskPriority[], void>({
      query: () => ({
        url: '/umbraco/api/Task/GetTaskPriorities',
      }),
      transformResponse: (response: unknown) => {
        if (!Array.isArray(response)) {
          throw new Error('Failed to load task priorities.');
        }

        return toCamelCase(response) as TaskPriority[];
      },
    }),
    createTasksForTeam: builder.mutation<unknown[], CreateTasksForTeamRequest>({
      query: ({ projectId, nodeKey, input }) => ({
        url: '/umbraco/api/ProjectTasks/CreateTasksForAllTeamMembers',
        method: 'POST',
        params: { projectId, nodeKey },
        body: input,
      }),
      transformResponse: (response: ApiResponse<unknown[]>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data ?? []) as unknown[];
        }

        throw new Error(response.message || 'Failed to create tasks for team members.');
      },
    }),
    getAutomaticWorkLogEmployeeOptions: builder.query<AutomaticWorkLogEmployeeOption[], number>({
      query: (projectId) => ({
        url: '/umbraco/api/ProjectTasks/GetAutomaticWorkLogEmployeeOptions',
        params: { projectId },
      }),
      transformResponse: (response: ApiResponse<AutomaticWorkLogEmployeeOption[]>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data ?? []) as AutomaticWorkLogEmployeeOption[];
        }

        throw new Error(response.message || 'Failed to load active employees.');
      },
    }),
    createTasksAndWorkLogsForEmployees: builder.mutation<
      CreateTasksAndWorkLogsResult,
      CreateTasksAndWorkLogsInput
    >({
      query: (input) => ({
        url: '/umbraco/api/ProjectTasks/CreateTasksAndWorkLogsForEmployees',
        method: 'POST',
        body: input,
      }),
      transformResponse: (response: ApiResponse<CreateTasksAndWorkLogsResult>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as CreateTasksAndWorkLogsResult;
        }

        throw new Error(response.message || 'Failed to create tasks and work logs.');
      },
    }),
    getMissingEmployeeRates: builder.mutation<
      MissingEmployeeRatesResult,
      MissingEmployeeRatesRequest
    >({
      query: (request) => ({
        url: '/umbraco/api/MissingEmployeeRates/GetMissingEmployeeRates',
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: ApiResponse<MissingEmployeeRatesResult>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as MissingEmployeeRatesResult;
        }
        throw new Error(extractErrorMessage(response, 'Failed to load missing employee rates.'));
      },
    }),
    createMissingEmployeeRate: builder.mutation<
      CreateMissingEmployeeRateResult,
      CreateMissingEmployeeRateRequest
    >({
      query: (request) => ({
        url: '/umbraco/api/MissingEmployeeRates/CreateMissingEmployeeRate',
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: ApiResponse<CreateMissingEmployeeRateResult>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as CreateMissingEmployeeRateResult;
        }
        throw new Error(extractErrorMessage(response, 'Failed to create employee rate.'));
      },
    }),
    /**
     * Fetch lookup data for relation fields (selectgrid)
     * Cached per nodeKey + fieldKey combination
     * Lookups rarely change, so cache for 10 minutes
     * Returns both a simple map (for display) and full data array (for grid panel)
     */
    getLookup: builder.query<LookupResponse, LookupParams>({
      query: ({ nodeKey, fieldKey, objectId, parentId, forDropdown, selectedEntity }) => ({
        url: `${GRID_API}/GetLookup`,
        params: {
          nodeKey,
          fieldKey,
          objectId: objectId ?? 0, // Always send objectId, default to 0
          ...(parentId !== undefined && parentId !== null && String(parentId).length > 0
            ? { parentId }
            : {}),
          ...(forDropdown ? { forDropdown } : {}),
          ...(selectedEntity ? { selectedEntity } : {}),
        },
      }),
      transformResponse: (response: unknown, _meta, params) => {
        const lookupData: LookupData = {};
        const lookupArray: LookupDataItem[] = [];
        const r = response as Record<string, unknown>;
        const dataArray = (r.data as Record<string, unknown> | undefined)?.data || r.data || [];

        if (Array.isArray(dataArray)) {
          dataArray.forEach((item: Record<string, unknown>) => {
            // Backend returns {Key: 'id', Value: 'displayName', IsDeleted: false}
            // OR {Id: 'id', Fields: [{Name: 'fieldName', StringValue: 'value'}]}
            let id: string;
            let displayValue: string;

            if (item.Key !== undefined && item.Value !== undefined) {
              id = item.Key as string;
              displayValue = item.Value as string;
              lookupData[id] = displayValue;

              // Store Key/Value items as full objects so cell templates can access
              // domain properties like IsDeleted that the backend returns alongside Key/Value
              lookupArray.push({
                Id: id,
                DeletedAt: (item.DeletedAt as string | null | undefined) ?? null,
                DisplayText: displayValue,
                Fields: [],
                // Preserve extra properties (IsDeleted, etc.) for cell template access
                ...Object.fromEntries(
                  Object.entries(item).filter(([k]) => k !== 'Key' && k !== 'Value')
                ),
              } as LookupDataItem);
            } else if (item.Id !== undefined) {
              id = item.Id as string;
              displayValue = id;

              if (params.displayFieldName && Array.isArray(item.Fields)) {
                const displayField = item.Fields.find(
                  (f: { Name: string; StringValue?: string }) => f.Name === params.displayFieldName
                );
                if (displayField?.StringValue) {
                  displayValue = displayField.StringValue;
                }
              }
            } else {
              return;
            }

            lookupData[id] = displayValue;

            // Store full item for grid panel (only if we have Fields array)
            if (Array.isArray(item.Fields)) {
              lookupArray.push({
                Id: item.Id as string,
                DeletedAt: (item.DeletedAt as string | null | undefined) ?? null,
                DisplayText: displayValue,
                Fields: item.Fields as LookupDataItem['Fields'],
              });
            }
          });
        }

        const map = buildFilterLookupMap(
          { map: lookupData, array: lookupArray },
          params.defaultFilters
        );
        const allowedIds = new Set(Object.keys(map));
        return {
          map,
          array: lookupArray.filter((item) => allowedIds.has(String(item.Id))),
          allMap: lookupData,
          allArray: lookupArray,
        };
      },
      providesTags: (_result, _error, params) => [
        { type: 'Lookup', id: `${params.nodeKey}-${params.fieldKey}` },
      ],
      keepUnusedDataFor: 600,
    }),

    /**
     * Fetch grid data with automatic lookup resolution
     * Optimized to prevent UI freezing on large datasets
     */
    getGridData: builder.query<GridData, GridDataParams>({
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        buildGridDataCacheKey(endpointName, queryArgs),
      async queryFn({ options, columnMetadata }, api, _extraOptions, baseQuery) {
        try {
          const timeoutMs = 60000;
          let timeoutId: ReturnType<typeof setTimeout>;
          const timeoutPromise = new Promise((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('Request timed out after 60 seconds')),
              timeoutMs
            );
          });

          let gridResult: { data?: unknown; error?: unknown };
          try {
            gridResult = (await Promise.race([
              baseQuery({
                url: `${GRID_API}/get`,
                method: 'POST',
                body: options,
              }),
              timeoutPromise,
            ])) as { data?: unknown; error?: unknown };
          } finally {
            clearTimeout(timeoutId!);
          }

          if (gridResult.error) {
            return { error: gridResult.error as FetchBaseQueryError };
          }

          const parsed = extractGridResponse(gridResult.data as Record<string, unknown>);
          if (parsed.error) {
            return {
              error: { status: 'CUSTOM_ERROR', error: parsed.error },
            };
          }

          const { rawData: fullRawData, totalCount, summary } = parsed;

          // Apply a hard cap on the number of rows processed client-side
          // to avoid excessive memory usage / UI freezes when the backend
          // returns a very large dataset.
          const MAX_GRID_ROWS = 10000;
          const isRowLimitExceeded =
            Array.isArray(fullRawData) && fullRawData.length > MAX_GRID_ROWS;
          const rawData = isRowLimitExceeded ? fullRawData.slice(0, MAX_GRID_ROWS) : fullRawData;
          if (isRowLimitExceeded) {
            console.warn(
              `Grid data truncated to ${MAX_GRID_ROWS} rows on client; ` +
                `original rows: ${fullRawData.length}`
            );
          }

          // Process lookups
          const lookupMaps = new Map<string, LookupData>();
          const filterLookupMaps = new Map<string, LookupData>();
          const rawLookupItemsMap = new Map<string, LookupDataItem[]>();

          if (columnMetadata && Array.isArray(columnMetadata)) {
            const relationFields = columnMetadata.filter((col) => {
              return isLookupColumn(col);
            });

            if (relationFields.length > 0) {
              // Process lookups in batches to avoid overwhelming the browser
              const BATCH_SIZE = 5; // Process 5 lookups at a time

              for (let i = 0; i < relationFields.length; i += BATCH_SIZE) {
                const batch = relationFields.slice(i, i + BATCH_SIZE);

                const batchPromises = batch.map(async (field) => {
                  if (!field.key || !field.dataField) return;
                  const displayFieldName = field.additionalData?.displayExpr;

                  try {
                    // Add timeout for individual lookup requests (15 seconds)
                    // If a lookup takes longer than 15s, there's likely a backend issue
                    const subscription = api.dispatch(
                      gridApi.endpoints.getLookup.initiate(
                        {
                          nodeKey: options.nodeKey,
                          fieldKey: field.key,
                          displayFieldName,
                          defaultFilters: field.defaultFilters,
                          parentId: options.parentId,
                        },
                        { subscribe: false }
                      )
                    );

                    let lookupTimeoutId: ReturnType<typeof setTimeout>;
                    const lookupTimeout = new Promise((_, reject) => {
                      lookupTimeoutId = setTimeout(() => {
                        subscription.abort();
                        reject(new Error(`Lookup timeout for ${field.key}`));
                      }, 15000);
                    });

                    let lookupResult: { data?: LookupResponse };
                    try {
                      lookupResult = (await Promise.race([subscription, lookupTimeout])) as {
                        data?: LookupResponse;
                      };
                    } finally {
                      clearTimeout(lookupTimeoutId!);
                    }

                    // Keep an empty map for inaccessible or empty lookups so cells do not
                    // fall back to displaying the underlying relation ID.
                    const lookupMap = lookupResult.data?.allMap ?? lookupResult.data?.map ?? {};
                    lookupMaps.set(field.dataField, lookupMap);
                    if (lookupResult.data?.allArray?.length) {
                      rawLookupItemsMap.set(field.dataField, lookupResult.data.allArray);
                    }

                    if (!lookupResult.data) return;

                    filterLookupMaps.set(
                      field.dataField,
                      buildFilterLookupMap(lookupResult.data, field.defaultFilters)
                    );
                  } catch {
                    // A failed lookup is still a lookup column; render its values as empty.
                    lookupMaps.set(field.dataField, {});
                    // Continue processing other lookups even if one fails
                  }
                });

                await Promise.all(batchPromises);
              }
            }
          }

          const flattenedData = flattenRawData(rawData);

          const lookupMapsObject: Record<string, Record<string, string>> = {};
          const filterLookupMapsObject: Record<string, Record<string, string>> = {};
          lookupMaps.forEach((value, key) => {
            lookupMapsObject[key] = value;
          });
          filterLookupMaps.forEach((value, key) => {
            filterLookupMapsObject[key] = value;
          });

          const rawLookupItemsObject: Record<string, LookupDataItem[]> = {};
          rawLookupItemsMap.forEach((value, key) => {
            rawLookupItemsObject[key] = value;
          });

          return {
            data: {
              data: flattenedData,
              totalCount,
              summary,
              lookupMaps: lookupMapsObject,
              filterLookupMaps: filterLookupMapsObject,
              rawLookupItems: rawLookupItemsObject,
            },
          };
        } catch (error: unknown) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error instanceof Error ? error.message : 'Failed to load grid data',
            },
          };
        }
      },
      providesTags: (_result, _error, { options }) => [
        {
          type: 'GridData',
          id: `node:${options.nodeKey}`,
        },
        {
          type: 'GridData',
          id: JSON.stringify({
            nodeKey: options.nodeKey,
            parentId: options.parentId,
            startDate: options.startDate,
            endDate: options.endDate,
            toShowDeleted: options.toShowDeleted,
            rowKeys: options.rowKeys,
          }),
        },
      ],
      keepUnusedDataFor: 600,
    }),

    /** Fetch an auxiliary configuration table through the existing grid endpoint. */
    getConfigurationTable: builder.query<GridData, { nodeKey: string }>({
      async queryFn({ nodeKey }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `${GRID_API}/get`,
          method: 'POST',
          body: { nodeKey },
        });

        if (result.error) return { error: result.error as FetchBaseQueryError };

        const parsed = extractGridResponse(result.data as Record<string, unknown>);
        if (parsed.error) {
          return { error: { status: 'CUSTOM_ERROR' as const, error: parsed.error } };
        }

        return {
          data: {
            data: flattenRawData(parsed.rawData),
            totalCount: parsed.totalCount,
            summary: parsed.summary,
          },
        };
      },
      providesTags: (_result, _error, { nodeKey }) => [
        { type: 'GridData', id: `node:${nodeKey}` },
        { type: 'GridData', id: `configuration:${nodeKey}` },
      ],
      keepUnusedDataFor: 3600,
    }),

    /**
     * Save entity (create or update)
     * Invalidates grid data cache to trigger refetch
     */
    saveEntity: builder.mutation<SaveEntityResponse, SaveEntityRequest>({
      query: ({
        invalidateEntity: _invalidateEntity,
        gridNodeKeyToInvalidate: _gridNodeKeyToInvalidate,
        ...entity
      }) => ({
        url: `${GRID_API}/save`,
        method: 'POST',
        body: entity,
      }),
      invalidatesTags: (
        result,
        error,
        { Id, NodeKey, invalidateEntity = true, gridNodeKeyToInvalidate }
      ) => {
        const saveSucceeded = !error && result?.data?.Code === 1;

        return [
          ...(saveSucceeded ? [{ type: 'GridData' as const, id: `node:${NodeKey}` }] : []),
          ...(saveSucceeded && gridNodeKeyToInvalidate
            ? [{ type: 'GridData' as const, id: `node:${gridNodeKeyToInvalidate}` }]
            : []),
          ...(saveSucceeded && invalidateEntity
            ? [{ type: 'Entity' as const, id: `${NodeKey}-${Id}` }]
            : []),
        ];
      },
    }),

    /**
     * Save multiple entities in batch (create or update)
     * Invalidates grid data cache to trigger refetch
     */
    saveBatch: builder.mutation<SaveEntityResponse, Entity[]>({
      query: (entities) => ({
        url: `${GRID_API}/SaveBatch`,
        method: 'POST',
        body: entities,
      }),
      invalidatesTags: (_result, _error, entities) => {
        const tags: Array<{ type: 'GridData'; id: string } | { type: 'Entity'; id: string }> = [];
        const invalidatedNodeKeys = new Set<string>();

        entities.forEach((entity) => {
          if (entity.NodeKey && !invalidatedNodeKeys.has(entity.NodeKey)) {
            invalidatedNodeKeys.add(entity.NodeKey);
            tags.push({ type: 'GridData' as const, id: `node:${entity.NodeKey}` });
          }
        });

        // Invalidate each entity's cache
        entities.forEach((entity) => {
          if (entity.Id && entity.NodeKey) {
            tags.push({ type: 'Entity' as const, id: `${entity.NodeKey}-${entity.Id}` });
          }
        });
        return tags;
      },
    }),

    updateBatch: builder.mutation<SaveEntityResponse, UpdateBatchRequest>({
      query: (request) => ({
        url: `${GRID_API}/UpdateBatch`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: SaveEntityResponse) => {
        if (response.data?.Code !== 1) {
          throw new Error(response.data?.Error || 'Failed to update selected records.');
        }
        return response;
      },
      invalidatesTags: (_result, _error, { nodeKey, keys }) => [
        { type: 'GridData' as const, id: `node:${nodeKey}` },
        ...keys.map((key) => ({ type: 'Entity' as const, id: `${nodeKey}-${key}` })),
      ],
    }),

    /**
     * Delete entity
     * Invalidates grid data cache to trigger refetch
     */
    deleteEntity: builder.mutation<SaveEntityResponse, { id: string; nodeKey: string }>({
      query: ({ id, nodeKey }) => ({
        url: `${GRID_API}/delete`,
        method: 'DELETE',
        body: { Id: id, NodeKey: nodeKey },
      }),
      invalidatesTags: (_result, _error, { nodeKey }) => [
        { type: 'GridData' as const, id: `node:${nodeKey}` },
      ],
    }),

    /**
     * Delete multiple entities in batch
     * Invalidates grid data cache to trigger refetch
     */
    deleteBatch: builder.mutation<SaveEntityResponse, Pick<Entity, 'Id' | 'NodeKey'>[]>({
      query: (entities) => ({
        url: `${GRID_API}/DeleteBatch`,
        method: 'POST',
        body: entities,
      }),
      invalidatesTags: (_result, _error, entities) => {
        const invalidatedNodeKeys = new Set<string>();
        entities.forEach((entity) => {
          if (entity.NodeKey) {
            invalidatedNodeKeys.add(entity.NodeKey);
          }
        });

        return Array.from(invalidatedNodeKeys).map((nodeKey) => ({
          type: 'GridData' as const,
          id: `node:${nodeKey}`,
        }));
      },
    }),

    /**
     * Restore a soft-deleted entity
     * Invalidates grid data cache to trigger refetch
     */
    restoreEntity: builder.mutation<SaveEntityResponse, { id: string; nodeKey: string }>({
      query: ({ id, nodeKey }) => ({
        url: `${GRID_API}/RestoreEntity`,
        method: 'POST',
        body: { Id: id, NodeKey: nodeKey },
      }),
      invalidatesTags: (_result, _error, { nodeKey }) => [
        { type: 'GridData' as const, id: `node:${nodeKey}` },
      ],
    }),

    /**
     * Archive entity by ID
     * Invalidates grid data cache to trigger refetch
     */
    archiveEntity: builder.mutation<SaveEntityResponse, { id: string; nodeKey: string }>({
      query: ({ id, nodeKey }) => ({
        url: `${GRID_API}/Archive`,
        method: 'POST',
        body: { Id: id, NodeKey: nodeKey },
      }),
      invalidatesTags: (_result, _error, { id, nodeKey }) => [
        { type: 'GridData' as const, id: `node:${nodeKey}` },
        { type: 'Entity' as const, id: `${nodeKey}-${id}` },
      ],
    }),

    /**
     * Fetch detail form metadata
     * Returns layout structure and field definitions for detail form
     */
    getDetailMetadata: builder.query<DetailMetadata, GetDetailMetadataParams>({
      query: (params) => ({
        url: '/umbraco/api/structure/GetDetailMetadata',
        params,
      }),
      transformResponse: (response: ApiResponse<DetailMetadata>) => {
        if (response.code === 1) {
          // Convert PascalCase keys to camelCase
          return toCamelCase(response.data) as DetailMetadata;
        }
        throw new Error(response.message || 'Failed to load detail metadata');
      },
      providesTags: (_result, _error, params) => [
        {
          type: 'DetailMetadata',
          id: params.detailFormId || params.detailFormKey,
        },
      ],
      keepUnusedDataFor: Infinity,
    }),

    getCurrencyRate: builder.query<number, CurrencyRateRequest>({
      query: ({ date, nameApi, fromCurrency, toCurrency }) => ({
        url: '/umbraco/api/Currency/Get',
        params: { date, nameAPI: nameApi, fromCurrency, toCurrency },
      }),
      transformResponse: (response: CurrencyRateResponse) => response.data,
    }),

    /**
     * Fetch single entity by ID using the dedicated GetOne endpoint
     */
    getEntity: builder.query<Record<string, unknown>, { nodeKey: string; id: string }>({
      async queryFn({ nodeKey, id }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `${GRID_API}/GetOne`,
          method: 'GET',
          params: { nodeKey, id },
        });

        if (result.error) return { error: result.error as FetchBaseQueryError };

        const parsed = extractGridResponse(result.data as Record<string, unknown>);
        if (parsed.error) {
          return { error: { status: 'CUSTOM_ERROR' as const, error: parsed.error } };
        }

        if (parsed.rawData.length === 0) {
          return { error: { status: 404 as const, data: 'Entity not found' } };
        }

        const flattenedData = flattenRawData(parsed.rawData);
        return { data: flattenedData[0] };
      },
      providesTags: (_result, _error, { nodeKey, id }) => [
        { type: 'Entity', id: `${nodeKey}-${id}` },
      ],
      // Force proper cache keying by parameters
      serializeQueryArgs: ({ queryArgs }) => {
        return `${queryArgs.nodeKey}-${queryArgs.id}`;
      },
      // Always refetch when arguments change
      forceRefetch: ({ currentArg, previousArg }) => {
        return currentArg?.id !== previousArg?.id;
      },
    }),

    /**
     * Fetch paginated data for a selectgrid dropdown
     * Reuses the same grid/get endpoint as the main grid
     */
    getDropdownGridData: builder.query<GridData, DropdownGridDataParams>({
      async queryFn({ gridId, skip, take, filter, sort }, _api, _extraOptions, baseQuery) {
        try {
          const body: Record<string, unknown> = { nodeKey: gridId, skip, take };
          if (filter) {
            body.filter = filter;
          }
          if (sort && sort.length > 0) {
            body.sort = sort;
          }

          let timeoutId: ReturnType<typeof setTimeout>;
          const timeoutPromise = new Promise((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('Dropdown request timed out after 30 seconds')),
              30000
            );
          });

          let result: { data?: unknown; error?: unknown };
          try {
            result = (await Promise.race([
              baseQuery({
                url: `${GRID_API}/get`,
                method: 'POST',
                body,
              }),
              timeoutPromise,
            ])) as { data?: unknown; error?: unknown };
          } finally {
            clearTimeout(timeoutId!);
          }

          if (result.error) {
            return { error: result.error as FetchBaseQueryError };
          }

          const parsed = extractGridResponse(result.data as Record<string, unknown>);
          if (parsed.error) {
            return {
              error: { status: 'CUSTOM_ERROR', error: parsed.error },
            };
          }

          return {
            data: {
              data: flattenRawData(parsed.rawData),
              totalCount: parsed.totalCount,
            },
          };
        } catch (error: unknown) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error instanceof Error ? error.message : 'Failed to load dropdown data',
            },
          };
        }
      },
      providesTags: (_result, _error, { gridId }) => [
        { type: 'GridData', id: `dropdown-${gridId}` },
      ],
      keepUnusedDataFor: 120,
    }),

    /**
     * Copy selected rows
     * Backend either persists copies (e.g. CrmInvoices) or returns field data for inline creation
     */
    copyRows: builder.mutation<CopyRowResponse, { nodeKey: string; rowKeys: string[] }>({
      query: ({ nodeKey, rowKeys }) => ({
        url: `${GRID_API}/CopyRow`,
        method: 'POST',
        body: { nodeKey, rowKeys },
      }),
      invalidatesTags: (_result, _error, { nodeKey }) => [
        { type: 'GridData' as const, id: `node:${nodeKey}` },
      ],
    }),

    getPresignedImageUrl: builder.query<string, string>({
      query: (key) => ({
        url: '/umbraco/api/PreSignedImage/GetPresignedUrlByKey',
        params: { key },
      }),
      transformResponse: (response: unknown) => {
        // Endpoint returns a plain JSON string
        return response as string;
      },
      keepUnusedDataFor: 300,
    }),

    deleteTrackingScreenshot: builder.mutation<
      DeleteScreenshotResponse,
      { id: string; nodeKey: string }
    >({
      async queryFn({ id }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `${TRACKER_API}/DeleteScreenshotTrackingDataAsync`,
          method: 'DELETE',
          params: { id },
        });

        if (result.error) return { error: result.error as FetchBaseQueryError };

        const data = result.data as DeleteScreenshotResponse;
        const success = data?.Success ?? data?.success ?? false;

        if (!success) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: data?.Error?.ErrorCode || 'ScreenshotDeleteFailed',
              data: data?.Error,
            } as FetchBaseQueryError,
          };
        }

        return { data };
      },
      invalidatesTags: (result, _error, { nodeKey }) =>
        result?.Success || result?.success
          ? [{ type: 'GridData' as const, id: `node:${nodeKey}` }]
          : [],
    }),

    getSpecification: builder.query<GetSpecificationResponse, number>({
      query: (assetId) => ({
        url: '/umbraco/api/AssetSpecification/Get',
        params: { assetId },
      }),
      transformResponse: (response: unknown): GetSpecificationResponse => {
        const payload = response as Record<string, unknown>;
        const code = Number(payload.Code ?? payload.code ?? -1);
        const inner = (payload.data ??
          payload.Data ??
          payload) as Partial<GetSpecificationResponse>;
        return {
          Data: Array.isArray(inner.Data) ? (inner.Data as SpecField[]) : [],
          SpecTypes: Array.isArray(inner.SpecTypes) ? (inner.SpecTypes as SpecType[]) : [],
          Code: code,
        };
      },

      providesTags: (_result, _error, assetId) => [
        { type: 'Entity' as const, id: `spec-${assetId}` },
      ],
      keepUnusedDataFor: 0,
    }),

    saveSpecification: builder.mutation<void, SaveSpecificationRequest>({
      query: ({ assetId, key, values }) => ({
        url: '/umbraco/api/AssetSpecification/SaveSpecifications',
        method: 'POST',
        body: { assetId, key, values },
      }),
      transformResponse: (response: unknown): void => {
        const payload = response as Record<string, unknown>;
        const code = payload.Code ?? payload.code;
        if (code !== undefined && Number(code) < 0) {
          throw new Error(
            typeof (payload.Data ?? payload.data) === 'string'
              ? String(payload.Data ?? payload.data)
              : 'Failed to save specifications.'
          );
        }
      },
      invalidatesTags: (_result, _error, { assetId }) => [
        { type: 'Entity' as const, id: `spec-${assetId}` },
      ],
    }),

    /**
     * Generate and send payroll documents via email
     * Generates PDF from template and sends to employee email addresses
     * Returns count of sent/skipped/failed with employee names
     */
    generateAndSendPayroll: builder.mutation<PayrollDeliveryResponse, PayrollDeliveryRequest>({
      query: (request) => ({
        url: '/umbraco/api/Payroll/GenerateAndSendPayroll',
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: unknown) => {
        const data = response as Record<string, unknown>;
        // Backend returns a raw payload with PascalCase properties (Sent/Skipped/Failed/...).
        return {
          sent: ((data.Sent ?? data.sent) as number) ?? 0,
          skipped: ((data.Skipped ?? data.skipped) as number) ?? 0,
          failed: ((data.Failed ?? data.failed) as number) ?? 0,
          skippedNames: ((data.SkippedNames ?? data.skippedNames) as string[]) ?? [],
          failedNames: ((data.FailedNames ?? data.failedNames) as string[]) ?? [],
        };
      },
    }),
  }),
});

export const {
  useGetGridDataQuery,
  useLazyGetConfigurationTableQuery,
  useGetLookupQuery,
  useSaveEntityMutation,
  useSaveBatchMutation,
  useUpdateBatchMutation,
  useDeleteEntityMutation,
  useDeleteBatchMutation,
  useRestoreEntityMutation,
  useArchiveEntityMutation,
  useGetDetailMetadataQuery,
  useLazyGetCurrencyRateQuery,
  useGetEntityQuery,
  useGetDropdownGridDataQuery,
  useCopyRowsMutation,
  useDeleteTrackingScreenshotMutation,
  useGetPresignedImageUrlQuery,
  useGetRelationCommandMetadataQuery,
  useCalculateSalaryMutation,
  useGenerateInvoicesMutation,
  useGetTaskPrioritiesQuery,
  useCreateTasksForTeamMutation,
  useGetAutomaticWorkLogEmployeeOptionsQuery,
  useCreateTasksAndWorkLogsForEmployeesMutation,
  useGetMissingEmployeeRatesMutation,
  useCreateMissingEmployeeRateMutation,
  useGenerateAndSendPayrollMutation,
  useGenerateAutoInvoiceMutation,
  useGetSpecificationQuery,
  useSaveSpecificationMutation,
} = gridApi;

export const {
  endpoints: { getGridData, getLookup, getConfigurationTable },
  reducerPath,
  reducer,
  middleware,
} = gridApi;
